import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useGlobalContext } from "../../GlobalContext";
import { Bell, Volume2, LogOut, VolumeX, ListTodo, MapPin } from "lucide-react";
import Logo from "../../Images/Packky.png";
import ConfirmLogoutModal from "../Utils/./ConfirmLogoutModal";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";

export default function Header({ onAddTaskClick, showOld, setShowOld }) {
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";

  const {
    isMuted,
    toggleMute,
    notificationCount,
    clearNotificationCount,
    setIsAddTaskOpen,
    logoutAdmin,
  } = useGlobalContext();

  const { fetchDriverTeams } = useDriverTeams();
  const navigate = useNavigate();

  const handleAdminLogout = () => {
    logoutAdmin();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    fetchDriverTeams();
  }, []);

  const setTaskView = () => {
    if (typeof setShowOld === "function") setShowOld(false);
    else console.error("setShowOld is not a function!", setShowOld);
  };

  const setMapView = () => {
    if (typeof setShowOld === "function") setShowOld(true);
    else console.error("setShowOld is not a function!", setShowOld);
  };

  return (
    <div className="bg-yellow-50 text-white w-full shadow">
  <div className="relative flex items-center min-h-12 py-2 px-2 sm:px-3">

    {/* LEFT: Segmented switch */}
    <div className="flex items-center min-w-0">
      {isDashboard && typeof setShowOld === "function" && (
        <div
          role="tablist"
          aria-label="Switch between Task and Map view"
          className="
            relative inline-grid grid-cols-2
            w-[170px] sm:w-[200px] md:w-[220px]
            rounded-full bg-white
            border border-[#fad9c0]
            p-1
          "
        >
          {/* Sliding pill */}
          <span
            aria-hidden="true"
            style={{
              width: "calc(50% - 0.25rem)",
              transform: showOld
                ? "translateX(100%)"
                : "translateX(0%)",
            }}
            className="
              absolute top-1 bottom-1 left-1
              rounded-full
              transition-transform duration-300 ease-in-out
              bg-white shadow
            "
          />

          {/* Task */}
          <button
            type="button"
            role="tab"
            aria-selected={!showOld}
            onClick={setTaskView}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") setMapView();
              if (e.key === "Enter" || e.key === " ") setTaskView();
            }}
            className={`
              relative z-10
              flex items-center justify-center
              gap-1 sm:gap-2
              px-2 sm:px-3
              py-1.5 sm:py-2
              rounded-full
              transition-colors
              text-[#F97316]
            `}
          >
            <ListTodo className="h-4 w-4 shrink-0" />

            <span className="text-xs sm:text-sm font-medium">
              Task
            </span>
          </button>

          {/* Map */}
          <button
            type="button"
            role="tab"
            aria-selected={showOld}
            onClick={setMapView}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") setTaskView();
              if (e.key === "Enter" || e.key === " ") setMapView();
            }}
            className={`
              relative z-10
              flex items-center justify-center
              gap-1 sm:gap-2
              px-2 sm:px-3
              py-1.5 sm:py-2
              rounded-full
              transition-colors
              text-[#F97316]
            `}
          >
            <MapPin className="h-4 w-4 shrink-0" />

            <span className="text-xs sm:text-sm font-medium">
              Map
            </span>
          </button>
        </div>
      )}
    </div>

    {/* CENTER: Logo */}
    <div className="absolute left-1/2 -translate-x-1/2">
      <img
        src={Logo}
        alt="Logo"
        className="
          h-5
          xs:h-6
          sm:h-7
          md:h-8
          w-auto
          max-w-[90px]
          sm:max-w-[120px]
          object-contain
        "
      />
    </div>

    {/* RIGHT: Actions */}
    <div
      className="
        ml-auto
        flex items-center
        gap-0.5 sm:gap-1 md:gap-2
        shrink-0
      "
    >
      {/* Add Task */}
      {isDashboard && (
        <button
          type="button"
          className="
            bg-orange-500
            hover:bg-orange-600
            px-2 sm:px-3
            py-1.5 sm:py-1
            rounded
            text-xs sm:text-sm
            text-white
            whitespace-nowrap
          "
          onClick={(e) => {
            e.currentTarget.blur();
            setIsAddTaskOpen(true);
          }}
        >
          <span className="hidden xs:inline">
            Add New Task
          </span>

          <span className="xs:hidden">
            + Task
          </span>
        </button>
      )}

      {/* Mute */}
      <button
        type="button"
        onClick={toggleMute}
        title={
          isMuted
            ? "Unmute Notifications"
            : "Mute Notifications"
        }
        className="
          p-1.5 sm:p-2
          rounded
          hover:bg-orange-100
          transition
        "
      >
        {isMuted ? (
          <VolumeX className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
        ) : (
          <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
        )}
      </button>

      {/* Notifications */}
      <button
        type="button"
        onClick={() => {
          clearNotificationCount();
          navigate("/notifications");
        }}
        className="
          relative
          p-1.5 sm:p-2
          rounded
          hover:bg-orange-100
          transition
        "
        title="Notifications"
      >
        <Bell
          className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600"
        />

        {notificationCount > 0 && (
          <span
            className="
              absolute
              -top-0.5 sm:-top-1
              -right-0.5 sm:-right-1
              min-w-[15px] sm:min-w-[18px]
              h-[15px] sm:h-[18px]
              px-1
              rounded-full
              bg-red-500
              text-white
              text-[9px] sm:text-[10px]
              font-bold
              flex items-center justify-center
              leading-none
              shadow
            "
          >
            {notificationCount > 99
              ? "99+"
              : notificationCount}
          </span>
        )}
      </button>

      {/* Logout */}
      <ConfirmLogoutModal
        onConfirm={handleAdminLogout}
        trigger={
          <button
            type="button"
            className="
              p-1.5 sm:p-2
              rounded
              hover:bg-orange-100
              transition
            "
          >
            <LogOut
              className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600"
            />
          </button>
        }
      />
    </div>
  </div>
</div>
  );
}
